import type React from "react";
import { ArrowRight, Bot, BrainCircuit, CheckCircle2, Coins, Download, Layers3, Network, ShieldCheck, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { HomeCardItem } from "./types";
import { CardIcon, Kicker } from "./HomeShared";

const previewItems = [
  { labelKey: "home.previewModels", icon: BrainCircuit, valueKey: "home.previewModelsValue" },
  { labelKey: "home.previewSkills", icon: Layers3, valueKey: "home.previewSkillsValue" },
  { labelKey: "home.previewSearch", icon: Network, valueKey: "home.previewSearchValue" },
  { labelKey: "home.previewCredits", icon: Coins, valueKey: "home.previewCreditsValue" },
];

interface HomeHeroProps {
  highlights: HomeCardItem[];
  latestVersion?: string;
  onDownload: () => void;
  onLogin: () => void;
}

export function HomeHero({ highlights, latestVersion, onDownload, onLogin }: HomeHeroProps) {
  const { t } = useTranslation("auth");
  return (
    <>
      <section className="relative overflow-hidden bg-[linear-gradient(112deg,var(--color-bg-surface)_0%,var(--home-hero-mid)_56%,var(--color-primary-soft)_100%)] dark:bg-[linear-gradient(112deg,var(--color-bg-page)_0%,var(--color-bg-base)_58%,var(--color-bg-elevated)_100%)]">
        <div className="relative mx-auto max-w-[1296px] px-5 py-14 sm:px-8 lg:min-h-[620px] lg:px-0 lg:py-0">
          <div className="grid items-center gap-10 lg:min-h-[620px] lg:grid-cols-[500px_1fr] lg:gap-[38px]">
            <div className="max-w-[500px]">
              <Kicker>{t("home.eyebrow")}</Kicker>
              <h1 className="mt-4 whitespace-pre-line font-display text-[3rem] font-bold leading-[1.08] tracking-normal text-text-primary sm:text-[3.25rem]">
                {t("home.title")}
              </h1>
              <p className="mt-4 max-w-[476px] text-base leading-7 text-text-secondary">{t("home.subtitle")}</p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <button type="button" onClick={onDownload} className="flex h-[52px] items-center justify-center gap-2 rounded-lg bg-action-primary px-5 text-[15px] font-bold text-action-primary-text shadow-raised transition-colors hover:bg-action-primary-hover sm:min-w-[221px]">
                  {t("home.viewReleases")}<Download className="h-4 w-4" />
                </button>
                <button type="button" onClick={onLogin} className="flex h-[52px] items-center justify-center gap-2 rounded-lg border border-border-default bg-bg-surface px-5 text-[15px] font-semibold text-text-primary transition-colors hover:bg-bg-elevated">
                  {t("home.secondaryCta")}<ArrowRight className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-4 flex items-start gap-2 text-[10px] leading-4 text-text-muted">
                <ShieldCheck className="mt-px h-3.5 w-3.5 shrink-0 text-success" /><span>{t("home.trustNote")}</span>
              </div>
            </div>
            <div className="relative min-h-[430px] lg:min-h-[500px]"><PlatformPreview latestVersion={latestVersion} /></div>
          </div>
          <div className="absolute bottom-7 right-10 hidden font-mono text-[9px] font-medium uppercase text-text-muted lg:block">{t("home.figureAnnotation")}</div>
        </div>
      </section>
      <section className="border-y border-border-subtle bg-bg-surface">
        <div className="mx-auto grid max-w-[1296px] px-5 sm:px-8 lg:grid-cols-[280px_repeat(3,1fr)] lg:px-0">
          <div className="border-b border-border-subtle py-7 lg:border-b-0 lg:border-r lg:pr-9">
            <div className="font-mono text-[10px] font-semibold uppercase text-primary-ink">{t("home.proofLabel")}</div>
            <p className="mt-2 whitespace-pre-line font-display text-[25px] font-bold leading-[1.12] text-text-primary">{t("home.proofTitle")}</p>
          </div>
          {highlights.map((item) => (
            <article key={item.title} className="flex min-h-24 gap-4 border-b border-border-subtle py-7 last:border-b-0 lg:border-b-0 lg:border-r lg:px-6 lg:last:border-r-0">
              <CardIcon icon={item.icon} className="h-[38px] w-[38px] rounded-md bg-primary-soft text-primary-ink" />
              <div><h2 className="text-sm font-bold text-text-primary">{item.title}</h2><p className="mt-1 text-[11px] leading-4 text-text-secondary">{item.description}</p></div>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

function PlatformPreview({ latestVersion }: { latestVersion?: string }) {
  const { t } = useTranslation("auth");
  return (
    <div className="relative flex h-full items-center lg:absolute lg:inset-y-0 lg:left-0 lg:right-[-72px]">
      <div className="w-full overflow-hidden rounded-[10px] border border-border-subtle bg-bg-surface shadow-overlay">
        <div className="flex h-[46px] items-center justify-between border-b border-border-subtle bg-bg-elevated px-4">
          <div className="flex items-center gap-2" aria-hidden="true"><span className="h-3 w-3 rounded-full bg-danger" /><span className="h-3 w-3 rounded-full bg-warning" /><span className="h-3 w-3 rounded-full bg-success" /></div>
          <div className="flex items-center gap-4"><span className="hidden font-mono text-[9px] font-medium uppercase text-text-muted sm:block">{t("home.previewTitle")}</span><span className="flex items-center gap-1.5 text-[8px] font-semibold uppercase text-success"><span className="h-1.5 w-1.5 rounded-full bg-success" />{t("home.previewConnected")}</span></div>
        </div>
        <div className="grid min-h-[454px] grid-cols-1 sm:grid-cols-[176px_1fr]">
          <aside className="border-b border-white/15 bg-[var(--home-ink)] p-3.5 text-white sm:border-b-0 sm:border-r">
            <div className="mb-3 pl-2 pt-1"><div className="text-[13px] font-bold">{t("home.previewWorkspace")}</div><div className="mt-1 font-mono text-[8px] uppercase text-text-muted">{t("home.previewWorkspaceMeta")}</div></div>
            {previewItems.map((item, index) => (
              <div key={item.labelKey} className={`mb-2 flex min-h-[54px] items-center gap-3 rounded-md border px-3 text-xs ${index === 0 ? "border-white/15 bg-white/10 text-white" : "border-transparent text-[var(--home-footer-muted)]"}`}>
                <item.icon className="h-4 w-4 shrink-0" /><span className="min-w-0"><span className="block truncate font-semibold">{t(item.labelKey)}</span><span className="block truncate font-mono text-[8px] text-text-muted">{t(item.valueKey)}</span></span>
              </div>
            ))}
          </aside>
          <div className="bg-bg-surface p-[22px]">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div><div className="font-display text-[22px] font-bold text-text-primary">{t("home.previewPanelTitle")}</div><div className="mt-1 text-[11px] text-text-muted">{t("home.previewPanelSubtitle")}</div></div>
              <span className="rounded-md bg-action-primary px-4 py-2 text-[11px] font-bold text-action-primary-text">{t("home.previewReady")}</span>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <PreviewTile icon={Bot} toneClass="bg-primary-soft text-primary-ink" label={t("home.previewTileModels")} value={t("home.previewTileModelsValue")} />
              <PreviewTile icon={Sparkles} toneClass="bg-success-soft text-success" label={t("home.previewTileAccess")} value={t("home.previewTileAccessValue")} />
              <PreviewTile icon={Network} toneClass="bg-primary-soft text-primary-ink" label={t("home.previewTileSearch")} value={t("home.previewTileSearchValue")} />
            </div>
            <div className="mt-4 overflow-hidden rounded-md border border-border-subtle">
              {[0, 1, 2, 3].map((row) => {
                const name = row === 3
                  ? latestVersion ? t("home.previewReleaseName", { version: latestVersion }) : t("home.releaseFallbackName")
                  : t(`home.previewRow${row + 1}Name`);
                return (
                  <div key={row} className="grid grid-cols-[1.35fr_1fr_1fr] gap-3 border-b border-border-subtle bg-bg-surface px-3.5 py-3 text-[10px] last:border-b-0">
                    <span className="truncate font-semibold text-text-primary" title={name}>{name}</span>
                    <span className="truncate font-mono text-[8px] uppercase text-text-muted">{t(`home.previewRow${row + 1}Type`)}</span>
                    <span className="flex items-center gap-1 truncate font-semibold text-primary-ink"><CheckCircle2 className="h-3 w-3 shrink-0" />{t(`home.previewRow${row + 1}Status`)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewTile({ icon: Icon, toneClass, label, value }: { icon: React.ComponentType<{ className?: string }>; toneClass: string; label: string; value: string }) {
  return (
    <div className="rounded-md border border-border-subtle bg-bg-elevated p-3.5">
      <div className={`mb-2 flex h-[30px] w-[30px] items-center justify-center rounded-md ${toneClass}`}><Icon className="h-4 w-4" /></div>
      <div className="text-xs font-bold text-text-primary">{label}</div><div className="mt-1 font-mono text-[8px] text-text-muted">{value}</div>
    </div>
  );
}
